import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  emailSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();
const onboardingStatusSchema = z.enum(['pre_boarding', 'in_progress', 'completed', 'cancelled']);
const onboardingTaskStatusSchema = z.enum([
  'pending',
  'in_progress',
  'completed',
  'overdue',
  'skipped',
]);
const assigneeRoleSchema = z.enum(['HR', 'MANAGER', 'IT', 'BUDDY', 'EMPLOYEE']);

/** Day-N pulse check-ins (ONB-16): a task template/task with `pulseDay` set is a pulse survey. */
export const ONBOARDING_PULSE_DAYS = [7, 30, 60, 90] as const;
export const onboardingPulseDaySchema = z.union([
  z.literal(7),
  z.literal(30),
  z.literal(60),
  z.literal(90),
]);
export type OnboardingPulseDay = z.infer<typeof onboardingPulseDaySchema>;

/** Largest batch `bulkCreateProcesses` accepts; bigger imports are split client-side. */
export const MAX_BULK_ONBOARDING_BATCH = 50;

export const onboardingPulseResponseSchema = z.object({
  id: uuidSchema,
  taskId: uuidSchema,
  pulseDay: onboardingPulseDaySchema,
  /** 1 (worst) to 5 (best). */
  rating: z.number().int().min(1).max(5),
  comment: z.string().nullable(),
  submittedAt: instantSchema,
});
export type OnboardingPulseResponse = z.infer<typeof onboardingPulseResponseSchema>;

export const onboardingResourceLinkSchema = z.object({
  label: z.string().trim().min(1).max(200),
  url: z.string().trim().url(),
});
export type OnboardingResourceLink = z.infer<typeof onboardingResourceLinkSchema>;

export const onboardingTaskSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  processId: uuidSchema,
  stageId: uuidSchema,
  stageName: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  assigneeRole: assigneeRoleSchema,
  assigneePersonId: uuidSchema.nullable(),
  assigneeName: z.string().nullable(),
  status: onboardingTaskStatusSchema,
  dueDate: dateOnlySchema,
  isRequired: z.boolean(),
  requiresUpload: z.boolean(),
  attachmentUrl: z.string().url().nullable(),
  completedAt: instantSchema.nullable(),
  completedByName: z.string().nullable(),
  notes: z.string().nullable(),
  /** True only for tasks whose whole point is a real message to the employee (e.g. "Send welcome
   * note") — the client renders compose-and-send instead of the generic status buttons for these. */
  requiresMessage: z.boolean(),
  /** True for a task whose work is handing a real register asset to the joiner (Assets module) — the
   * IT work queue shows the asset handover panel for it. Set per template task by HR. */
  assetHandover: z.boolean(),
  /** HR-curated company/induction links (handbook, training videos) — visible to the employee
   * regardless of this task's own assigneeRole, since Induction tasks are BUDDY/MANAGER-assigned
   * but the links are for the employee to open. */
  resourceLinks: z.array(onboardingResourceLinkSchema),
  /** 7/30/60/90 when this is a Day-N pulse survey (the client renders the rating form instead of
   * the generic status buttons); null for every ordinary task. */
  pulseDay: onboardingPulseDaySchema.nullable(),
  /** The employee's answer once submitted; null until then, and always null when `pulseDay` is. */
  pulseResponse: onboardingPulseResponseSchema.nullable(),
});
export type OnboardingTask = z.infer<typeof onboardingTaskSchema>;

export const onboardingQueueTaskSchema = onboardingTaskSchema.extend({
  employeeName: z.string(),
  /** The joiner's person id — what the asset handover call targets. */
  personId: uuidSchema,
  /** The joiner's designation, used to suggest an asset kit; null when none is set yet. */
  designation: z.string().nullable(),
  targetJoinDate: dateOnlySchema,
  departmentName: z.string().nullable(),
});
export type OnboardingQueueTask = z.infer<typeof onboardingQueueTaskSchema>;

export const onboardingStageSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  orderIndex: z.number().int(),
  tasks: z.array(onboardingTaskSchema),
});
export type OnboardingStage = z.infer<typeof onboardingStageSchema>;

export const onboardingTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  legalEntityId: uuidSchema.nullable(),
  locationId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  /** Probation length (days) applied when onboarding starts from this template. */
  probationDurationDays: z.number().int().positive(),
  stages: z.array(
    z.object({
      id: uuidSchema,
      name: z.string(),
      description: z.string().nullable(),
      orderIndex: z.number().int(),
      taskCount: z.number().int().nonnegative(),
    }),
  ),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type OnboardingTemplate = z.infer<typeof onboardingTemplateSchema>;

export const onboardingProcessSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  personId: uuidSchema,
  employeeCode: z.string().nullable(),
  employeeName: z.string(),
  email: emailSchema,
  departmentName: z.string().nullable(),
  designation: z.string().nullable(),
  managerName: z.string().nullable(),
  buddyName: z.string().nullable(),
  employmentStatus: z.string(),
  status: onboardingStatusSchema,
  targetJoinDate: dateOnlySchema,
  /** Null only if the employment was never on probation; kept (not cleared) after confirmation. */
  probationEndOn: dateOnlySchema.nullable(),
  confirmedOn: dateOnlySchema.nullable(),
  /** How many times probation has been pushed out — see `ProbationExtension`. */
  extensionCount: z.number().int().nonnegative(),
  completionPercent: z.number().int().min(0).max(100),
  overdueTaskCount: z.number().int().nonnegative(),
  stages: z.array(onboardingStageSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type OnboardingProcess = z.infer<typeof onboardingProcessSchema>;

export const onboardingAttentionTaskSchema = z.object({
  taskId: uuidSchema,
  processId: uuidSchema,
  employeeName: z.string(),
  title: z.string(),
  dueDate: dateOnlySchema,
  daysLate: z.number().int(),
  assigneeRole: assigneeRoleSchema,
});
export type OnboardingAttentionTask = z.infer<typeof onboardingAttentionTaskSchema>;

export const onboardingStageFunnelEntrySchema = z.object({
  stageName: z.string(),
  orderIndex: z.number().int(),
  hireCount: z.number().int().nonnegative(),
  completionPercent: z.number().int().min(0).max(100),
});
export type OnboardingStageFunnelEntry = z.infer<typeof onboardingStageFunnelEntrySchema>;

export const onboardingDashboardSchema = z.object({
  activeCount: z.number().int().nonnegative(),
  onTrackRate: z.number().min(0).max(100),
  overdueTaskCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  averageCompletion: z.number().int().min(0).max(100),
  processes: z.array(onboardingProcessSchema),
  stageFunnel: z.array(onboardingStageFunnelEntrySchema),
  avgTimeToProductivityDays: z.number().nonnegative().nullable(),
  overdueTasks: z.array(onboardingAttentionTaskSchema),
  missingDocumentTasks: z.array(onboardingAttentionTaskSchema),
});
export type OnboardingDashboard = z.infer<typeof onboardingDashboardSchema>;

export const listOnboardingProcessesQuerySchema = paginationQuerySchema.extend({
  status: onboardingStatusSchema.optional(),
  q: z.string().trim().max(200).optional(),
});
export const createOnboardingTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  legalEntityId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  isDefault: z.boolean().default(false),
  /** Org-level default probation length (days), applied when onboarding starts unless overridden
   * per employee/role. */
  probationDurationDays: z.number().int().positive().default(180),
  stages: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(200),
        description: z.string().trim().max(2_000).optional(),
        tasks: z
          .array(
            z.object({
              title: z.string().trim().min(1).max(200),
              description: z.string().trim().max(2_000).optional(),
              assigneeRole: assigneeRoleSchema,
              dueDaysOffset: z.number().int().min(-365).max(365).default(0),
              isRequired: z.boolean().default(true),
              requiresUpload: z.boolean().default(false),
              requiresMessage: z.boolean().default(false),
              assetHandover: z.boolean().default(false),
              pulseDay: onboardingPulseDaySchema.optional(),
            }),
          )
          .max(100),
      }),
    )
    .min(1)
    .max(20),
});
export const onboardingTaskTemplateSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  assigneeRole: assigneeRoleSchema,
  dueDaysOffset: z.number().int(),
  isRequired: z.boolean(),
  requiresUpload: z.boolean(),
  requiresMessage: z.boolean(),
  assetHandover: z.boolean(),
  resourceLinks: z.array(onboardingResourceLinkSchema),
  /** Set on the four Day-N pulse task templates; generated tasks copy it. */
  pulseDay: onboardingPulseDaySchema.nullable(),
  orderIndex: z.number().int(),
});
export type OnboardingTaskTemplate = z.infer<typeof onboardingTaskTemplateSchema>;

/** Full template for the authoring screen: stages WITH their task templates, plus how many hires
 * were generated from it. `onboardingTemplateSchema` (list/create/reorder) is deliberately left as is. */
export const onboardingTemplateDetailSchema = onboardingTemplateSchema
  .omit({ stages: true })
  .extend({
    stages: z.array(
      z.object({
        id: uuidSchema,
        name: z.string(),
        description: z.string().nullable(),
        orderIndex: z.number().int(),
        tasks: z.array(onboardingTaskTemplateSchema),
      }),
    ),
    hireCount: z.number().int().nonnegative(),
  });
export type OnboardingTemplateDetail = z.infer<typeof onboardingTemplateDetailSchema>;

export const updateOnboardingTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2_000).nullable().optional(),
  legalEntityId: uuidSchema.nullable().optional(),
  locationId: uuidSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
  probationDurationDays: z.number().int().positive().optional(),
});
export const duplicateOnboardingTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
});
export const createOnboardingStageBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
});
export const updateOnboardingStageBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2_000).nullable().optional(),
});
export const createOnboardingTaskTemplateBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  assigneeRole: assigneeRoleSchema,
  dueDaysOffset: z.number().int().min(-365).max(365).default(0),
  isRequired: z.boolean().default(true),
  requiresUpload: z.boolean().default(false),
  requiresMessage: z.boolean().default(false),
  assetHandover: z.boolean().default(false),
  resourceLinks: z.array(onboardingResourceLinkSchema).max(20).default([]),
  pulseDay: onboardingPulseDaySchema.nullable().optional(),
});
export const updateOnboardingTaskTemplateBodySchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2_000).nullable().optional(),
  assigneeRole: assigneeRoleSchema.optional(),
  dueDaysOffset: z.number().int().min(-365).max(365).optional(),
  isRequired: z.boolean().optional(),
  requiresUpload: z.boolean().optional(),
  requiresMessage: z.boolean().optional(),
  assetHandover: z.boolean().optional(),
  resourceLinks: z.array(onboardingResourceLinkSchema).max(20).optional(),
  pulseDay: onboardingPulseDaySchema.nullable().optional(),
});
export const createOnboardingProcessBodySchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: emailSchema,
  phone: z.string().trim().max(30).optional(),
  legalEntityId: uuidSchema,
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  managerEmploymentId: uuidSchema.optional(),
  buddyPersonId: uuidSchema.optional(),
  designation: z.string().trim().max(200).optional(),
  employmentType: z.string().trim().min(1).max(40).default('full_time'),
  targetJoinDate: dateOnlySchema,
  templateId: uuidSchema,
});
export const updateOnboardingTaskBodySchema = z.object({
  status: onboardingTaskStatusSchema.optional(),
  assigneePersonId: uuidSchema.nullable().optional(),
  notes: z.string().max(2_000).nullable().optional(),
  attachmentUrl: z.string().url().nullable().optional(),
  /** Present only when completing a `requiresMessage` task — sends a real `onboarding.welcome_note`
   * notification to the employee via `@hrms/notify`; see onboarding.ts's updateTask handler. Never
   * persisted verbatim as a column of its own — the caller who wants a record of it also passes the
   * same text as `notes`. */
  message: z.string().trim().min(1).max(2_000).optional(),
  /** Replaces the task's resourceLinks wholesale (HR-only, enforced in the route) — the client
   * always sends the full desired list, same pattern as `notes`. */
  resourceLinks: z.array(onboardingResourceLinkSchema).max(20).optional(),
});
/**
 * AI-drafted welcome note. The body carries drafting preferences ONLY — never a name, date or any
 * other fact about the hire: the API loads those itself from the task's own process, so nothing the
 * caller sends can widen what reaches the model (CLAUDE.md invariants 7 and 9).
 */
export const ONBOARDING_WELCOME_NOTE_TONES = ['warm', 'formal', 'casual'] as const;
export type OnboardingWelcomeNoteTone = (typeof ONBOARDING_WELCOME_NOTE_TONES)[number];
export const draftWelcomeNoteBodySchema = z.object({
  tone: z.enum(ONBOARDING_WELCOME_NOTE_TONES).optional(),
});
export const draftWelcomeNoteResponseSchema = z.object({
  /** Plain text, ready to drop into the compose box. Persists nothing and notifies nobody — HR
   * reviews/edits it and sends it through the ordinary `updateTask` `message` flow. */
  draft: z.string(),
});
export type DraftWelcomeNoteResponse = z.infer<typeof draftWelcomeNoteResponseSchema>;

/**
 * Start onboarding for someone who is ALREADY a real Person/Employment in this org — the normal
 * case once a hire arrives from Recruitment or was added directly to the Employee Directory.
 * Deliberately separate from `createProcess` above rather than an optional `employmentId` on it:
 * that endpoint's whole body (firstName/lastName/email/legalEntityId/...) is person-creation
 * input and none of it applies here, so branching one schema on presence/absence of `employmentId`
 * would make every other field's "required" status a lie depending on a field the type system
 * can't see. `targetJoinDate` is read from the existing `Employment.joinedOn` — not accepted here
 * — for the same single-profile-invariant reason `createProcess` never lets a caller restate a
 * fact that already lives on the Employment row.
 */
export const attachOnboardingProcessBodySchema = z.object({
  employmentId: uuidSchema,
  templateId: uuidSchema,
  buddyPersonId: uuidSchema.optional(),
});
export const listOnboardingTasksQuerySchema = paginationQuerySchema.extend({
  assigneeRole: assigneeRoleSchema.optional(),
  status: onboardingTaskStatusSchema.optional(),
});
export const reorderOnboardingTemplateBodySchema = z.object({
  stageIds: z.array(uuidSchema).min(1).max(20),
});
export const createOnboardingUploadBodySchema = z.object({
  contentType: z.enum(['application/pdf', 'image/jpeg', 'image/png']),
  sizeBytes: z.number().int().positive().max(10_485_760),
});
export const onboardingUploadTicketSchema = z.object({
  uploadUrl: z.string().url(),
  attachmentUrl: z.string().url(),
  expiresIn: z.number().int().positive(),
});

export const updateOnboardingProcessBodySchema = z.object({
  buddyPersonId: uuidSchema.nullable().optional(),
  managerEmploymentId: uuidSchema.nullable().optional(),
  targetJoinDate: dateOnlySchema.optional(),
  status: onboardingStatusSchema.optional(),
});

export const createProcessTaskBodySchema = z.object({
  stageId: uuidSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  assigneeRole: assigneeRoleSchema.default('EMPLOYEE'),
  dueDate: dateOnlySchema,
  isRequired: z.boolean().default(true),
  requiresUpload: z.boolean().default(false),
});

export const onboardingPeerSchema = z.object({
  personId: uuidSchema,
  employmentId: uuidSchema,
  name: z.string(),
  designation: z.string().nullable(),
  departmentName: z.string().nullable(),
});

export const onboardingNoteSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  processId: uuidSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  createdAt: instantSchema,
});
export type OnboardingNote = z.infer<typeof onboardingNoteSchema>;
export const createOnboardingNoteBodySchema = z.object({
  body: z.string().trim().min(1).max(2_000),
});

/** The plaintext password is returned exactly once, in this response, and never stored or logged
 * anywhere — same posture as a presigned upload URL. HR is expected to hand it to the new hire and
 * discard it; the hire's own `mustChangePassword` flag (already true here) forces a change on
 * first login through the existing self-service change-password flow. */
export const requestDocumentBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  dueDate: dateOnlySchema,
});

/**
 * Invite-only sign-in, like Add Employee: no password is made or shown. The hire gets a one-time
 * set-password link (7 days), emailed to their PERSONAL address (the work email is a newly generated
 * company address that may not be a working mailbox yet). Following the link sets their own
 * password.
 */
export const createLoginCredentialResponseSchema = z.object({
  /** The work email they will sign in with. */
  email: emailSchema,
  /** True only when the link was actually emailed. */
  emailSent: z.boolean(),
  /** The personal address the link was emailed to; null when nothing was sent. */
  sentTo: z.string().nullable(),
  /**
   * The one-time sign-in link, returned ONLY when it was not emailed (HR chose not to, or the send
   * failed) so HR can hand it over; null when it went out by email. Shown once; never log it.
   */
  inviteUrl: z.string().nullable(),
  expiresInDays: z.number().int(),
});

export const workEmailSuggestionSchema = z.object({
  workEmail: z.string(),
  /** Where credentials would be sent: `Person.personalEmail ?? Person.email`. */
  personalEmail: z.string(),
});

/** A recruitment candidate whose offer was accepted and whose `stage` was moved to `hired`, but
 * who has no Person/Employment yet — the single-source-of-truth handoff point between Recruitment
 * and Onboarding (Keka's "no duplicate PII entry" principle, CLAUDE.md invariant 4). Once HR starts
 * onboarding from one of these, the resulting Employment is what makes them a real employee. */
export const hiredCandidateSchema = z.object({
  candidateId: uuidSchema,
  firstName: z.string(),
  lastName: z.string(),
  email: emailSchema,
  phone: z.string().nullable(),
  postingTitle: z.string(),
  departmentId: uuidSchema,
  legalEntityId: uuidSchema,
  targetJoinDate: dateOnlySchema.nullable(),
});

export const createLoginCredentialBodySchema = z.object({
  /** Omit to let the server generate `<first>.<l>@<domain>`; when given, HR edited it. */
  workEmail: emailSchema.optional(),
  sendEmail: z.boolean().default(true),
});

// ---------------------------------------------------------------------------
// Bulk start (ONB-15)
// ---------------------------------------------------------------------------

/**
 * Start onboarding for many NEW hires at once. Each item is exactly a `createProcess` body and is
 * processed independently in its own transaction: one bad row never rolls back the others, and the
 * response reports every row. Financial-style bulk import (invariant 10), so the route requires an
 * `Idempotency-Key`; a retried request returns the original results and creates nothing twice.
 */
export const bulkCreateOnboardingProcessesBodySchema = z.object({
  items: z.array(createOnboardingProcessBodySchema).min(1).max(MAX_BULK_ONBOARDING_BATCH),
});

/**
 * Why a row was not created. 'skipped' = nothing to do, safe to ignore (`duplicate_in_batch`,
 * `already_onboarding`); 'failed' = HR must fix the row and resubmit it (the rest).
 */
export const bulkOnboardingReasonSchema = z.enum([
  'duplicate_in_batch',
  'already_onboarding',
  'template_not_found',
  'legal_entity_not_found',
  'manager_not_found',
  'buddy_not_found',
  'invalid',
  'error',
]);
export type BulkOnboardingReason = z.infer<typeof bulkOnboardingReasonSchema>;

const bulkRowBase = { index: z.number().int().nonnegative(), email: emailSchema };
export const bulkOnboardingItemResultSchema = z.discriminatedUnion('status', [
  z.object({
    ...bulkRowBase,
    status: z.literal('created'),
    processId: uuidSchema,
    employmentId: uuidSchema,
  }),
  z.object({
    ...bulkRowBase,
    status: z.literal('skipped'),
    reason: bulkOnboardingReasonSchema,
    message: z.string(),
  }),
  z.object({
    ...bulkRowBase,
    status: z.literal('failed'),
    reason: bulkOnboardingReasonSchema,
    message: z.string(),
  }),
]);
export type BulkOnboardingItemResult = z.infer<typeof bulkOnboardingItemResultSchema>;

export const bulkCreateOnboardingProcessesResponseSchema = z.object({
  /** One entry per submitted item, in submission order (`index` is the position in `items`). */
  results: z.array(bulkOnboardingItemResultSchema),
  createdCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
});
export type BulkCreateOnboardingProcessesResponse = z.infer<
  typeof bulkCreateOnboardingProcessesResponseSchema
>;

// ---------------------------------------------------------------------------
// Pulse surveys (ONB-16)
// ---------------------------------------------------------------------------

/** The new hire answers a Day-N pulse task. Completes the task in the same transaction. */
export const submitOnboardingPulseBodySchema = z.object({
  rating: z.number().int().min(1).max(5),
  comment: z.string().trim().max(2_000).optional(),
});

/** HR-side view of one answer; carries the respondent, so `onboarding:manage` only. */
export const onboardingPulseResponseDetailSchema = onboardingPulseResponseSchema.extend({
  processId: uuidSchema,
  personId: uuidSchema,
  employeeName: z.string(),
  departmentName: z.string().nullable(),
});
export type OnboardingPulseResponseDetail = z.infer<typeof onboardingPulseResponseDetailSchema>;

export const listOnboardingPulseResponsesQuerySchema = paginationQuerySchema.extend({
  pulseDay: onboardingPulseDaySchema.optional(),
  processId: uuidSchema.optional(),
});

export const onboardingPulseDaySummarySchema = z.object({
  pulseDay: onboardingPulseDaySchema,
  responseCount: z.number().int().nonnegative(),
  /** Mean rating to one decimal place; null when nobody has responded yet. */
  averageRating: z.number().min(1).max(5).nullable(),
  /** Count of responses at each rating, index 0 = rating 1 ... index 4 = rating 5. */
  distribution: z.tuple([
    z.number().int().nonnegative(),
    z.number().int().nonnegative(),
    z.number().int().nonnegative(),
    z.number().int().nonnegative(),
    z.number().int().nonnegative(),
  ]),
});
/** One row per pulse day (7, 30, 60, 90), always all four, over onboarding processes in scope. */
export const onboardingPulseSummarySchema = z.object({
  days: z.array(onboardingPulseDaySummarySchema),
});
export type OnboardingPulseSummary = z.infer<typeof onboardingPulseSummarySchema>;

export const onboardingContract = c.router({
  getDashboard: {
    method: 'GET',
    path: '/onboarding/dashboard',
    responses: { 200: onboardingDashboardSchema, 401: errorBodySchema, 403: errorBodySchema },
    metadata: routeMeta('onboarding:manage'),
  },
  listProcesses: {
    method: 'GET',
    path: '/onboarding/processes',
    query: listOnboardingProcessesQuerySchema,
    responses: { 200: paginatedSchema(onboardingProcessSchema), 401: errorBodySchema },
    metadata: routeMeta('onboarding:read'),
  },
  getProcess: {
    method: 'GET',
    path: '/onboarding/processes/:processId',
    responses: { 200: onboardingProcessSchema, 401: errorBodySchema, 404: errorBodySchema },
    metadata: routeMeta('onboarding:read'),
  },
  updateProcess: {
    method: 'PATCH',
    path: '/onboarding/processes/:processId',
    body: updateOnboardingProcessBodySchema,
    responses: {
      200: onboardingProcessSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  createProcess: {
    method: 'POST',
    path: '/onboarding/processes',
    body: createOnboardingProcessBodySchema,
    responses: {
      201: onboardingProcessSchema,
      401: errorBodySchema,
      // PLAN_LIMIT_REACHED: the free-trial employee cap (see employees.create).
      402: errorBodySchema,
      403: errorBodySchema,
      409: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  attachProcess: {
    method: 'POST',
    path: '/onboarding/processes/attach',
    body: attachOnboardingProcessBodySchema,
    responses: {
      201: onboardingProcessSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      409: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  createTask: {
    method: 'POST',
    path: '/onboarding/processes/:processId/tasks',
    body: createProcessTaskBodySchema,
    responses: {
      201: onboardingTaskSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  updateTask: {
    method: 'PATCH',
    path: '/onboarding/tasks/:taskId',
    body: updateOnboardingTaskBodySchema,
    responses: {
      200: onboardingTaskSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  listTasks: {
    method: 'GET',
    path: '/onboarding/tasks',
    query: listOnboardingTasksQuerySchema,
    responses: {
      200: paginatedSchema(onboardingQueueTaskSchema),
      401: errorBodySchema,
      403: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  nudgeProcess: {
    method: 'POST',
    path: '/onboarding/processes/:processId/nudge',
    body: c.noBody(),
    responses: {
      204: c.noBody(),
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  draftWelcomeNote: {
    method: 'POST',
    path: '/onboarding/tasks/:taskId/welcome-note-draft',
    body: draftWelcomeNoteBodySchema,
    responses: {
      200: draftWelcomeNoteResponseSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      /** The task is not a compose-a-message ("Send welcome note") task — code CONFLICT. */
      409: errorBodySchema,
      /** The prompt tripped a blocking DLP rule — code VALIDATION_FAILED. */
      422: errorBodySchema,
      /** The model provider is rate limiting us — code AI_RATE_LIMITED. */
      429: errorBodySchema,
      /** The provider failed or returned an unusable draft — AI_PROVIDER_ERROR / AI_DRAFT_INVALID. */
      502: errorBodySchema,
      /** No AI provider is configured — code AI_NOT_CONFIGURED. Never a canned message. */
      503: errorBodySchema,
    },
    summary:
      'AI-drafted welcome note for a "Send welcome note" task — read-only, persists and notifies ' +
      'nothing; HR sends it through updateTask. 503 AI_NOT_CONFIGURED when no provider is set',
    metadata: routeMeta('onboarding:manage'),
  },
  listTemplates: {
    method: 'GET',
    path: '/onboarding/templates',
    query: paginationQuerySchema,
    responses: { 200: paginatedSchema(onboardingTemplateSchema), 401: errorBodySchema },
    metadata: routeMeta('onboarding:read'),
  },
  createTemplate: {
    method: 'POST',
    path: '/onboarding/templates',
    body: createOnboardingTemplateBodySchema,
    responses: { 201: onboardingTemplateSchema, 401: errorBodySchema, 403: errorBodySchema },
    metadata: routeMeta('onboarding:manage'),
  },
  reorderTemplate: {
    method: 'PATCH',
    path: '/onboarding/templates/:templateId/reorder',
    body: reorderOnboardingTemplateBodySchema,
    responses: {
      200: onboardingTemplateSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  duplicateTemplate: {
    method: 'POST',
    path: '/onboarding/templates/:templateId/duplicate',
    body: duplicateOnboardingTemplateBodySchema,
    responses: {
      201: onboardingTemplateDetailSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  getTemplate: {
    method: 'GET',
    path: '/onboarding/templates/:templateId',
    responses: {
      200: onboardingTemplateDetailSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:read'),
  },
  updateTemplate: {
    method: 'PATCH',
    path: '/onboarding/templates/:templateId',
    body: updateOnboardingTemplateBodySchema,
    responses: {
      200: onboardingTemplateDetailSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      409: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  addStage: {
    method: 'POST',
    path: '/onboarding/templates/:templateId/stages',
    body: createOnboardingStageBodySchema,
    responses: {
      201: onboardingTemplateDetailSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  updateStage: {
    method: 'PATCH',
    path: '/onboarding/templates/:templateId/stages/:stageId',
    body: updateOnboardingStageBodySchema,
    responses: {
      200: onboardingTemplateDetailSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  deleteStage: {
    method: 'DELETE',
    path: '/onboarding/templates/:templateId/stages/:stageId',
    body: c.noBody(),
    responses: {
      204: c.noBody(),
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      409: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  createTemplateTask: {
    method: 'POST',
    path: '/onboarding/templates/:templateId/stages/:stageId/tasks',
    body: createOnboardingTaskTemplateBodySchema,
    responses: {
      201: onboardingTemplateDetailSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  updateTemplateTask: {
    method: 'PATCH',
    path: '/onboarding/templates/:templateId/tasks/:taskTemplateId',
    body: updateOnboardingTaskTemplateBodySchema,
    responses: {
      200: onboardingTemplateDetailSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  deleteTemplateTask: {
    method: 'DELETE',
    path: '/onboarding/templates/:templateId/tasks/:taskTemplateId',
    body: c.noBody(),
    responses: {
      204: c.noBody(),
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  createUploadTicket: {
    method: 'POST',
    path: '/onboarding/tasks/:taskId/upload-ticket',
    body: createOnboardingUploadBodySchema,
    responses: {
      201: onboardingUploadTicketSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      422: errorBodySchema,
    },
    metadata: routeMeta(null),
  },
  listPeers: {
    method: 'GET',
    path: '/onboarding/peers',
    responses: { 200: z.array(onboardingPeerSchema), 401: errorBodySchema },
    metadata: routeMeta(null),
  },
  listNotes: {
    method: 'GET',
    path: '/onboarding/processes/:processId/notes',
    responses: {
      200: z.array(onboardingNoteSchema),
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  createNote: {
    method: 'POST',
    path: '/onboarding/processes/:processId/notes',
    body: createOnboardingNoteBodySchema,
    responses: {
      201: onboardingNoteSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    metadata: routeMeta('onboarding:manage'),
  },
  getWorkEmailSuggestion: {
    method: 'GET',
    path: '/onboarding/processes/:processId/work-email-suggestion',
    responses: {
      200: workEmailSuggestionSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      409: errorBodySchema,
    },
    summary: 'The auto-generated work email and the personal address credentials would be sent to',
    metadata: routeMeta('onboarding:manage'),
  },
  createLoginCredential: {
    method: 'POST',
    path: '/onboarding/processes/:processId/credential',
    body: createLoginCredentialBodySchema,
    responses: {
      201: createLoginCredentialResponseSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      409: errorBodySchema,
    },
    summary: 'HR creates first-time login access for a new hire — 409 if one already exists',
    metadata: routeMeta('onboarding:manage'),
  },
  listHiredCandidates: {
    method: 'GET',
    path: '/onboarding/hired-candidates',
    responses: { 200: z.array(hiredCandidateSchema), 401: errorBodySchema, 403: errorBodySchema },
    summary: 'Recruitment candidates marked hired who have not yet been converted into an employee',
    metadata: routeMeta('onboarding:manage'),
  },
  requestDocument: {
    method: 'POST',
    path: '/onboarding/processes/:processId/documents',
    body: requestDocumentBodySchema,
    responses: {
      201: onboardingTaskSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    summary:
      'HR asks the employee to upload a specific document — an ad-hoc task in the Documents stage',
    metadata: routeMeta('onboarding:manage'),
  },
  bulkCreateProcesses: {
    method: 'POST',
    path: '/onboarding/processes/bulk',
    headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
    body: bulkCreateOnboardingProcessesBodySchema,
    responses: {
      // 200, not 201: the batch itself always "succeeds"; per-row outcomes are in `results`.
      200: bulkCreateOnboardingProcessesResponseSchema,
      400: errorBodySchema,
      401: errorBodySchema,
      403: errorBodySchema,
      409: errorBodySchema,
    },
    summary:
      'Start onboarding for up to MAX_BULK_ONBOARDING_BATCH new hires — per-row created / skipped / failed; needs Idempotency-Key',
    metadata: routeMeta('onboarding:manage'),
  },
  submitPulseResponse: {
    method: 'POST',
    path: '/onboarding/tasks/:taskId/pulse-response',
    body: submitOnboardingPulseBodySchema,
    responses: {
      201: onboardingPulseResponseSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      409: errorBodySchema,
    },
    summary:
      'The new hire answers their own Day-N pulse task (rating 1-5) — completes the task; 409 if already answered or not a pulse task',
    // Every employee holds onboarding:read (it gates /my-onboarding); the handler additionally
    // requires the caller to be the process's own person.
    metadata: routeMeta('onboarding:read'),
  },
  listPulseResponses: {
    method: 'GET',
    path: '/onboarding/pulse-responses',
    query: listOnboardingPulseResponsesQuerySchema,
    responses: {
      200: paginatedSchema(onboardingPulseResponseDetailSchema),
      401: errorBodySchema,
      403: errorBodySchema,
    },
    summary: 'HR view of Day-7/30/60/90 pulse answers with the respondent',
    metadata: routeMeta('onboarding:manage'),
  },
  getPulseSummary: {
    method: 'GET',
    path: '/onboarding/pulse-summary',
    responses: { 200: onboardingPulseSummarySchema, 401: errorBodySchema, 403: errorBodySchema },
    summary: 'Average rating and distribution per pulse day across onboarding processes',
    metadata: routeMeta('onboarding:manage'),
  },
});
